<template>
  <div class="wrapper__detail">
    <q-linear-progress v-if="this.reloading" indeterminate />
    <div class="header">
      <div class="header__top">
        <BackBtn @click="this.handleBack" />
        <div style="display: flex; gap: 10px; align-items: center">
          <q-btn-toggle
            v-if="this.apartments?.length"
            v-model="this.activeFloorFlatId"
            toggle-color="primary"
            :options="getFlatOptions()"
            @update:model-value="onFlatChange"
            size="sm"
          />
          <Clickable
            v-if="this.apartments?.length"
            :disable="!this.activeFlat?.kvlist"
            :link="true"
            :href="`${PAGES.imageUrl}?url=${this.activeFlat?.kvlist}&title=${this.activeData?.resident_name}, ${this.activeData?.entrance_name}, этаж ${this.activeData?.floor}, кв ${this.activeFlat?.flat_num}`"
            class="header__link"
          >
            <q-btn
              size="sm"
              color="primary"
              :disable="!this.activeFlat?.kvlist"
            >
              <q-tooltip> Квартирный лист </q-tooltip>
              <q-icon size="2em" color="#fff" name="list_alt_icon" />
            </q-btn>
          </Clickable>
        </div>
      </div>

      <div
        class="header__head"
        :style="(this.floorMapId) ? `width: calc(100% - 315px);` : 'width: 100%'"
      >
        <div>
          <h1 class="title" @click="() => this.reloadData()">
            Схема этажа {{ this.activeData?.floor }}
            <q-icon name="replay" class="reloadIcon" />
          </h1>
          <div class="resident" v-if="!!this.activeData">
            <span>{{ this.activeData?.resident_name }}</span>
            |
            <span>{{ this.activeData?.entrance_name }}</span>
          </div>
        </div>
        <div
          v-if="!!this.floorMapId"
          style="display: flex; gap: 10px; align-items: flex-end"
        >
          <CustomSelect
            :options="this.activeFlat ? this.flatRooms?.rooms || [] : this.floorRooms"
            label="Комната"
            optionValueKey="room_id"
            optionLabelKey="room_name"
            :value="this.updateParamBody?.room_id || ''"
            @change="value => this.onUpdateParamBodyChange('room_id', value)"
            style="width: 120px;"
          />
          <CustomSelect
            v-if="this.editingMode"
            :options="this.paramTypes || []"
            label="Тип"
            optionValueKey="floor_param_type_id"
            optionLabelKey="floor_param_type_name"
            :value="this.updateParamBody.floor_param_type_id || ''"
            style="width: 180px;"
            @change="value => this.onUpdateParamBodyChange('floor_param_type_id', value)"
          />
          <CustomDropdown
            v-if="!this.editingMode"
            :onClick="this.handleAction"
            :options="this.actions || []"
            title="Действия"
          />
        </div>
        <q-btn
          v-else-if="!this.deletingMode"
          size="sm"
          title="Изменить схему этажа"
          label="Изменить схему этажа"
          color="primary"
          @click="this.handleFloorMapChange"
        />
        <div class="asideActions" v-if="!!this.floorMapId">
          <q-btn
            size="sm"
            padding="xs"
            title="Загрузить с буффера"
            label="Загрузить с буффера"
            color="green"
            :loading="this.bufferLoading"
            @click="this.getBufferParamsData"
          />
          <q-btn
            size="sm"
            padding="xs"
            label="Проверить ошибки"
            color="red"
            :loading="this.errorLoading"
            @click="this.getFloorErrors"
          />
          <q-toggle
            v-if="!!this.floorSchema?.data || this.bufferParamsExist"
            label="Редактирование"
            :disable="!this.activeFloorFlatId"
            v-model="this.editingMode"
            size="xs"
          />
          <q-toggle
            v-if="!!this.floorSchema?.data || this.bufferParamsExist"
            :disable="!this.editingMode"
            label="Групповое ред."
            title="Групповое редактирование"
            size="xs"
            v-model="this.groupEditingMode"
          />
          <q-toggle
            v-if="!!this.floorSchema?.data"
            label="Удаление"
            size="xs"
            v-model="this.deletingMode"
          />
          <q-toggle
            v-if="!!this.floorSchema?.data"
            :disable="this.editingMode"
            label="Границы квартир"
            size="xs"
            v-model="this.flatBorders"
          />
        </div>
      </div>
    </div>
    <div
      :style="{...this.wrapperStyle, ...this.wrapperStyles}"
      v-if="this.floorSchema"
    >
      <v-stage
        :config="{...stageConfig, ...stageView}"
        ref="stage"
        @wheel="onWheel"
      >
        <v-layer>
          <v-image
            v-if="backgroundImage"
            :config="{ image: this.backgroundImage, 
                      y: 0, 
                      opacity: 0.5,  
                   }"
          />
          <v-line
            v-if="this.bufferParams?.lines?.length"
            v-for="(wall, index) in this.bufferParams.lines"
            :key="wall.floor_buffer_id"
            style="cursor: pointer;"
            :config="{
            points: this.flattenPoints(wall.points),
            stroke: this.getLineColor(wall, `buffer-${index}`, wall.floor_flat_id), 
            strokeWidth: (this.activeFloorFlatId && !this.editingMode) ? wall?.floor_flat_id === this.activeFloorFlatId ? 8 : 0 : 8,
            x: 0,
            y: 0, 
            lineJoin: 'round',
        }"
            @click="handleLineClick(wall, $event)"
            @mouseenter="onMouseEnter(wall, $event, `buffer-${index}`)"
            @mouseleave="onMouseLeave"
          />
          <v-line
            v-for="(wall, index) in this.floorSchema?.lines"
            :key="wall.floor_param_id"
            style="cursor: pointer;"
            :config="{
            points: this.flattenPoints(wall.points),
            stroke: this.getLineColor(wall, `param-${index}`, wall.floor_flat_id), 
            strokeWidth: this.getLineWidth(wall),
            x: 0,
            y: 0, 
            lineJoin: 'round',
            dash: wall?.floor_param_type_id_relative ? [10, 5] : []
        }"
            @click="handleLineClick(wall, $event)"
            @mouseenter="onMouseEnter(wall, $event, `param-${index}`)"
            @mouseleave="onMouseLeave"
          />
          <v-circle
            v-if="!!this.floorSchema?.circles?.length"
            v-for="(circle, index) in this.floorSchema?.circles"
            :config="{
                    x: circle.points[0],
                    y: circle.points[1],
                    radius: 10,
                    opacity: '1',
                    stroke: this.getLineColor(circle, `circle-${index}`, circle.floor_flat_id),
                    strokeWidth: this.getLineWidth(circle),
                  }"
            @click="handleLineClick(circle, $event)"
            @mouseenter="onMouseEnter(circle, $event, `circle-${index}`)"
            @mouseleave="onMouseLeave"
          />
          <v-circle
            v-if="!!this.bufferParams?.circles?.length"
            v-for="(circle, index) in this.bufferParams?.circles"
            :config="{
                    x: circle.points[0],
                    y: circle.points[1],
                    radius: 10,
                    opacity: '1',
                    stroke: '#000',
                    strokeWidth: (this.activeFloorFlatId && !this.editingMode) ? wall?.floor_flat_id === this.activeFloorFlatId ? 8 : 0 : 8,
                  }"
            @click="handleLineClick(circle, $event)"
            @mouseenter="onMouseEnter(circle, $event, `circle-${index}`)"
            @mouseleave="onMouseLeave"
          />
          <v-circle
            v-if="!!this.floorSchema?.walls_center?.length && !this.editingMode"
            v-for="room in this.floorSchema?.walls_center"
            :config="this.getCircleSettings(room)"
          />
          <v-text
            v-if="!!this.floorSchema?.walls_center?.length && !this.editingMode"
            v-for="room in this.floorSchema?.walls_center"
            :key="'label_' + room.flat_num"
            :config="{
              text: `кв ${room.flat_num}`,
              x: room.center_x,
              y: room.center_y,
              fontSize: 40,
              fontStyle: 'bold',
              fontFamily: 'sans-serif',
              fill: '#333',
              align: 'center',
              listening: false
            }"
          />
          <v-text
            v-if="!!this.floorSchema?.area_center?.length"
            v-for="areaCenter in this.floorSchema?.area_center"
            :key="'area_' + areaCenter.room_id"
            :config="{
              text: `${areaCenter.area}`,
              x: areaCenter.center_x - String(areaCenter.area)?.length * 4,
              y: areaCenter.center_y - 12,
              fontSize: (this.activeFloorFlatId) ? text?.floor_flat_id === this.activeFloorFlatId ? 20 : 0 : 20,
              fontStyle: 'bold',
              fontFamily: 'sans-serif',
              fill: '#333',
              align: 'center',
              listening: false
            }"
          />
          <v-text
            v-if="!!this.floorSchema?.texts?.length"
            v-for="text in this.floorSchema?.texts"
            :key="'frame_' + text.floor_param_id + text.frame_name"
            :config="{
              text: text.frame_name,
              x: text.center_point[0] - text.frame_name.length * 4,
              y: text.center_point[1],
              fontSize: (this.activeFloorFlatId && !this.editingMode) ? text?.floor_flat_id === this.activeFloorFlatId ? 12 : 0 : 12,
              fontStyle: 'bold',
              fontFamily: 'sans-serif',
              fill: '#333',
              align: 'center',
              listening: false
            }"
          />
        </v-layer>
      </v-stage>
      <q-card
        v-if="!!hoveredWall"
        class="tooltip"
        style="position: absolute; z-index: 9999"
        :style="this.getTooltipStyles()"
      >
        <div class="text-subtitle2">
          {{ `${this.deletingMode ? 'Удалить - ' : ''}${hoveredWall?.floor_param_type_name} ${hoveredWall?.floor_param_type_name_relative ? `, ${hoveredWall?.floor_param_type_name_relative}` :''}` }}
        </div>
      </q-card>
    </div>
    <q-linear-progress v-else-if="this.schemaFetching" indeterminate />
    <NotFoundText v-else-if="!this.schemaFetching" />
    <Legend
      v-if="!!this.floorMapId"
      :legend-fetching="this.legendFetching"
      :legend-info="this.legendInfo"
      :selectable="true"
      :reloadData="this.getFloorLegendData"
      :selected="this.activeFloorParamTypeIds"
      :onBlock="this.blockFloorParamType"
      :onSelect="value => this.setActiveParamTypeId(value)"
    />
  </div>
</template>

<script>
import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar';
import BackBtn from '@/components/common/BackBtn.vue';
import Clickable from '@/components/common/Clickable.vue';
import CustomDropdown from "@/components/common/CustomDropdown";
import NotFoundText from '@/components/common/NotFoundText.vue';
import CustomSelect from "@/components/form/CustomSelect";
import { API_URL, MODAL_NAMES, PAGES } from '@/constants';
import { appActions } from '@/services/store/modules/app_module/app_actions';
import Legend from "./Legend.vue";
import { blockParamType, deleteFloorMapLine, flattenPoints, getBufferParams, getFloorFlatRooms, getFloorLegendInfo, getFloorMapErrors, getFloorMapInfo, getFloorParamTypes, getFloorRooms, getFloorSchema, onKonvaWheel, recalculateFloorValues, updateFloorParam } from './services';

export default {
  name: "ResidentialFloorDetail",
  components: { BackBtn, NotFoundText, CustomDropdown, CustomSelect, Legend, Clickable },
  props: ['activeData', 'params', 'updateData'],
  data() {
    return {
      PAGES,
      schemaFetching: false,
      updatingParam: false,
      editingMode: false,
      groupEditingMode: false,
      deletingMode: false,
      deleteLoading: false,
      bufferLoading: false,
      errorLoading: false,
      reloading: false,
      flatRooms: null,
      floorRooms: null,
      paramTypes: [],
      bufferParams: [],
      updateParamBody: {room_id: null, floor_param_type_id: null},
      floorSchema: null,
      legendInfo: null,
      legendFetching: false,
      wrapperStyle: {
        position: 'relative',
      },
      stageConfig: {
        draggable: true,
        scaleX: 0.5,
        scaleY: 0.5,
      },
      floorMapId: null,
      objectInfo: null,
      activeFloorFlatId: null,
      activeFloorParamTypeIds: [],
      activeFlat: null,
      activeObjectId: null,
      activeBufferObjectId: null,
      backgroundImage: null,
      hoveredIndex: null,
      hoveredWall: null,
      flatBorders: false,
      tooltipX: 0,
      tooltipY: 0,
    };
  },
  computed:{
    apartments() {
      return this.floorSchema?.flat?.map(item => ({...item, label: `кв. ${item.flat_num}`}))
    },
    menuDrawerOpen() {
      return this.$store.state.app.menuDrawerOpen
    },
    bufferParamsExist() {
      return this.bufferParams?.lines?.length || this.bufferParams?.circles?.length
    },
    actions() {
      return [
        {label: 'Пересчитать', value: 'recalculate', disabled: !!this.legendFetching, onClick: this.recalculateValues},
        {label: 'Заменить по стояку', value: 'flat-grouping', disabled: !this.activeFloorFlatId, onClick: this.handleFlatGrouping},
        {label: 'Заменить по этажу', value: 'floor-grouping', onClick: this.handleGrouping, disabled: !this.activeData?.colour_id},
        {label: 'Редактировать', value: 'edit', onClick: () => this.handleFloorMapChange(true)},
      ]
    },
    stageView() {
      const view = {
        width: (window.innerWidth - 250) || 1170,
        height: (window.innerHeight - 205) || 700,
      }
      if(this.$store.state.app.menuDrawerOpen)
        view.width = window.innerWidth || 1170
      return view
    },
    wrapperStyles() {
      const view = {
        width: `${(window.innerWidth - 250) || 1170}px`,
        height: `${(window.innerHeight - 205) || 700}px`,
      }
      if(this.$store.state.app.menuDrawerOpen)
        view.width = `${window.innerWidth || 1170}px`
      return view
    },
  },
  methods: {
    onKonvaWheel,
    flattenPoints,
    getTooltipStyles() {
      if(this.menuDrawerOpen)
        return { left: this.tooltipX - 30 + 'px', top: this.tooltipY - 235 + 'px' }
      return { left: this.tooltipX - 280 + 'px', top: this.tooltipY - 235 + 'px' }
    },
    async getFloorSchemaData(floor_map_id) {
      this.schemaFetching = true
      const res = await getFloorSchema(floor_map_id)
      this.schemaFetching = false
      this.floorSchema = res || null
    },
    async getFloorLegendData() {
      await this.getFloorLegend(this.floorMapId, this.activeFlat?.flat_id || null, this.updateParamBody?.room_id || null)
    },
    async getFloorLegend(floor_map_id, flat_id = '', room_id = '') {
      this.legendFetching = true
      const res = await getFloorLegendInfo({floor_map_id, flat_id, room_id, project_type_id: this.activeData.project_type_id})
      this.legendFetching = false
      this.legendInfo = res || null
    },
    async recalculateValues() {
      if(this.legendFetching) return
      this.legendFetching = true
      const res = await recalculateFloorValues(this.floorMapI, {project_type_id: this.activeData.project_type_id})
      this.legendFetching = false
      this.legendInfo = res || null
    },
    async getFloorErrors() {
      this.errorLoading = true
      const res = await getFloorMapErrors(this.floorMapId)
      this.errorLoading = false
      if(!res) return
      const {empty_second_param_type, line_error} = res
      if(!empty_second_param_type && !line_error)
        return this.showSucceedSnackbar('Ошибок не найдено')

      this.$store.commit(appActions.showModal(MODAL_NAMES.FLOOR_MAP_ERRORS, {
        title: 'Список ошибок:',
        data: res
      }));
    },
    async blockFloorParamType(floor_param_type_id = null, all = false) {
      const params = {
        floor_map_id: this.floorMapId, flat_id: this.activeFlat?.flat_id || null, room_id: this.updateParamBody?.room_id || null
      }
      const body = {
        floor_map_id: this.floorMapId,
        floor_param_type_id, all
      }
      this.legendFetching = true
      const res = await blockParamType(body, params)
      this.legendFetching = false
      this.legendInfo = res || null
    },
    async getFloorRoomsData() {
      const res = await getFloorRooms(this.floorMapId)
      this.floorRooms = res || []
    },
    async getFlatRooms(flat_id = '') {
      if(!flat_id) return
      const res = await getFloorFlatRooms(flat_id)
      this.flatRooms = {flat_id, rooms: res || []}
    },
    async getFloorFloorMap(floor_map_id) {
      if(!floor_map_id) return
      const res = await getFloorMapInfo(floor_map_id)
      this.floorMapInfo = res
    },
    async getParamTypes() {
      const res = await getFloorParamTypes()
      this.paramTypes = res
    },
    async getBufferParamsData() {
      this.bufferLoading = true
      const res = await getBufferParams(this.floorMapId)
      this.bufferLoading = false
      this.bufferParams = res
      if(res?.length)
        this.editingMode = true
    },
    getFlatOptions() {
      const all = {
        label: 'Все',
        floor_flat_id: null,
        value: null,
      }
      if(!this.apartments?.length) return [all]
      return [all, ...this.apartments?.map(item => ({...item, value: item.floor_flat_id}))]
    },
    onFlatChange(floor_flat_id, flat) {
      if(!floor_flat_id) {
        this.editingMode = false
        this.activeFlat = null
        this.activeFloorFlatId = null || null
        this.getFloorLegendData()
        return
      }
      this.activeFlat = flat
      this.activeFloorFlatId = floor_flat_id || null
      this.flatBorders = false
      this.getFloorLegendData()
    },
    onUpdateParamBodyChange(key, value) {
      this.updateParamBody[key] = value
       if(key === 'room_id') {
         this.getFloorLegendData()
       }
    },
    updateFloorSchema(res) {
      if(res?.floor_map?.floor_map_id) {
         this.floorMapId = res?.floor_map?.floor_map_id
         this.floorMapInfo = res?.floor_map
        }
      this.floorSchema = res
      this.updateData()
    },
    handleBack() {
      this.floorSchema = null
      this.$emit('goBack')
    },
    setActiveParamTypeId(typeId) {
      if(this.activeFloorParamTypeIds.includes(typeId))
        return this.activeFloorParamTypeIds = this.activeFloorParamTypeIds.filter(item => item !== typeId)
      this.activeFloorParamTypeIds = [...this.activeFloorParamTypeIds, typeId]
    },
    async handleLineClick(wall) {
      if(!wall) return
      if(!this.editingMode) {
        if(wall.floor_param_id)
          this.activeObjectId = wall.floor_param_id
        else
          this.activeBufferObjectId = wall.floor_buffer_id
      }
      if(this.deletingMode) {
        this.deleteObject(wall)
        return
      }
      this.editingMode ? this.updateFloorParamData(wall) : this.showObjectInfoModal(wall)
    },
    getLineColor(wall, index, floor_flat_id) {
      if(this.hoveredIndex === index) return 'blue'
      if(!wall.floor_param_id && this.activeBufferObjectId === wall.floor_buffer_id) return '#970e0e'
      if(!wall.floor_param_id) return '#000'
      if(this.activeObjectId === wall.floor_param_id && !!wall.floor_param_id) return '#970e0e'
      if(this.flatBorders && this.floorSchema?.flatColors) return this.floorSchema?.flatColors[floor_flat_id]

      return wall?.floor_param_type_color || '#000'
    },
    getLineWidth(wall) {
      let width = 8
      if(!this.editingMode) {
        if(this.activeFloorParamTypeIds?.length) {
          const isExist = this.activeFloorParamTypeIds.includes(wall?.floor_param_type_id)
          if(!isExist)
            width = 0
        }
        if(this.activeFloorFlatId) {
          if(wall?.floor_flat_id !== this.activeFloorFlatId)
            width = 0
        }
        if(this.updateParamBody?.room_id) {
          if(wall?.room_id !== this.updateParamBody?.room_id)
            width = 0
        }
      }
      return width
    },
    onMouseLeave(evt) {
      this.hoveredWall = null;
      this.hoveredIndex = null;
      const stage = evt.target.getStage();
      stage.container().style.cursor = 'default';
    },
    onMouseEnter(wall, evt, index) {
      this.hoveredIndex = index
      this.hoveredWall = wall;
      const pointerPos = evt.evt; // native MouseEvent
      this.tooltipX = pointerPos.clientX + 10;
      this.tooltipY = pointerPos.clientY + 10;

      const stage = evt.target.getStage();
      stage.container().style.cursor = 'pointer';
    },
    showObjectInfoModal(wall) {
      if(!wall?.floor_param_id) {
        this.activeObjectId = null
        this.activeBufferObjectId = null
        return
      }
      this.$store.commit(appActions.showModal(MODAL_NAMES.FLOOR_SCHEMA_OBJECT_INFO, {
        title: `${wall?.floor_param_type_name || 'Информация об объекте'} (ID: ${wall?.floor_param_id})`,
        data: wall, params: {floor_map_id: this.floorMapId, project_type_id: this.activeData.project_type_id}, resident_id: this.activeData?.resident_id,
        onClose: () => {
          this.activeObjectId = null
          this.activeBufferObjectId = null
        },
        onSubmit: () => {
          this.getFloorLegendData()
          this.getFloorSchemaData(this.floorMapId)
          this.updateData()
        },
      }));
    },
    async updateFloorParamData(wall) {
      const { floor_param_id, floor_buffer_id } = wall
      const body = {...this.updateParamBody, floor_param_id, floor_buffer_id, floor_flat_id: this.activeFlat?.floor_flat_id,
        with_relative: !!this.groupEditingMode}

      this.updatingParam = true
      const res = await updateFloorParam(body, {floor_map_id: this.floorMapId})
      this.updatingParam = false
      if (!res) return
      this.getFloorLegendData()
      this.updateFloorSchema(res)
      this.showSucceedSnackbar()
      if(floor_buffer_id && this.bufferParams) {
        if(wall.coord_type === 'POINT') {
          this.bufferParams.circles = this.bufferParams?.circles?.filter(item => item.floor_buffer_id !== floor_buffer_id)
        } else {
          this.bufferParams.lines = this.bufferParams?.lines?.filter(item => item.floor_buffer_id !== floor_buffer_id)
        }
        if(this.groupEditingMode)
          return this.getBufferParamsData()
      }

    },
    async deleteObject(wall) {
      const { floor_param_id } = wall
      this.deleteLoading = true;
      const res = await deleteFloorMapLine(floor_param_id, {floor_map_id: this.floorMapId})
      this.deleteLoading = false;
      if(!res) return
      this.updateFloorSchema(res)
      this.getFloorLegendData()
      this.showSucceedSnackbar()
    },
    handleGrouping() {
      this.$store.commit(appActions.showModal(MODAL_NAMES.RESIDENTIAL_GROUPING, {
        title: 'Выберите этажи для смены',
         mode: 'floor-grouping',
        params: {...this.params, floor_map_id: this.floorMapId},
        data: { colour_id : this.activeData?.colour_id },
        onSubmit: this.updateFloorSchema,
      }));
    },
    handleFloorMapChange(isEdit = false) {
      let body = {level_num: this.activeData.floor, entrance: this.activeData.entrance, resident_id: this.activeData.resident_id}

      if(isEdit === true && this.floorMapInfo) {
        body = {...body, ...this.floorMapInfo}
      }

      this.$store.commit(appActions.showModal(MODAL_NAMES.AddFloorMap, {
        title: 'Изменить схему этажа',
        params: {entrance: this.activeData.entrance, resident_id: this.activeData.resident_id, project_type_id: this.activeData.project_type_id},
        body, isEdit: isEdit === true, floor_map_id: this.floorMapId,
        onSubmit: this.updateFloorSchema,
      }));
    },
    handleFlatGrouping() {
      if(!this.activeFlat) return
      this.$store.commit(appActions.showModal(MODAL_NAMES.RESIDENTIAL_GROUPING, {
        title: 'Выберите этажи для смены',
        mode: 'flat-grouping',
        params: {...this.params, floor_map_id: this.floorMapId},
        data: { ...this.activeData, floorFlatLength: this.floorSchema?.flat?.length, ...this.activeFlat,},
        onSubmit: (res) => {
          this.updateFloorSchema(res)
        },
      }));
    },
    loadBackground() {
      const src = this.floorSchema?.floor_map?.image_url
      if(!src) return
      const image = new Image();
      image.src = `${API_URL}${this.floorSchema?.floor_map?.image_url}`; // путь к PNG (можно и base64)
      image.onload = () => {
        this.backgroundImage = image;
      };
    },
    handleAction(action) {
      if(!action?.onClick) return
      action.onClick()
    },
    getCircleSettings(room) {
      const {flat_num} = room
      const flatNumLength = String(flat_num).length
      let settings = {
        x: room.center_x + 50,
        y: room.center_y + 17,
        radius: 60,
        listening: false
      }
      if(flatNumLength === 1) {
        settings.x = room.center_x + 40
        settings.radius = 55
      } else if(flatNumLength === 3) {
        settings.x = room.center_x + 61
        settings.radius = 67
      }
      return {
        ...settings,
        fill: '#fff',
        opacity: '.7',
        stroke: '#ccc',
        strokeWidth: 1
      }
    },
    onWheel(e) {
      const stage = this.$refs.stage.getNode();
      this.onKonvaWheel(e, stage)
    },
    async reloadData() {
      if(this.reloading) return
      this.reloading = true
      await this.getFloorSchemaData(this.activeData.floor_map_id)
      this.getFloorLegendData()
      await this.getFloorFloorMap(this.activeData.floor_map_id)
      this.reloading = false
    }
  },
  watch: {
    floorSchema: function () {
      this.loadBackground()
    },
    activeFlat(newVal) {
      if (newVal) {
        if(this.flatRooms?.flat_id !== newVal?.flat_id) {
          this.getFlatRooms(newVal?.flat_id)
        }
      }
    },
    editingMode(newValue) {
      if(newValue) this.deletingMode = false;
      if(newValue && !this.paramTypes?.length) {
        this.getParamTypes()
      }
      if(!newValue)
        this.groupEditingMode = false
    },
    deletingMode(newValue) {
      if(newValue) this.editingMode = false;
    },
  },
  mounted() {
    this.floorMapId = this.activeData.floor_map_id
    this.getFloorFloorMap(this.activeData.floor_map_id)
    this.getFloorSchemaData(this.activeData.floor_map_id)
    this.getFloorLegend(this.activeData.floor_map_id)
    this.getFloorRoomsData()
  },
  setup() {
    const {showSucceedSnackbar} = useCustomSnackbar()
    return {showSucceedSnackbar}
  }
};
</script>

<style scoped lang="scss">
.wrapper__detail {
  position: relative;
}
.header {
  padding: 20px;
  &__link {
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    i {
      display: inline-block;
    }
  }
}
.header__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 15px;
  position: relative;
}
.header__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: calc(100% - 315px);
}
.title {
  font-size: 30px;
  line-height: 37px;
  font-weight: 500;
  margin-top: 0;
  margin-bottom: 0;
  cursor: pointer;
  transition: all .3s;

  &:hover {
    color: rgb(5, 104, 0);
  }
}
.reloadIcon {
  font-size: 22px;
  color: rgb(5, 104, 0);
}
.resident {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #898989;

  span {
    font-size: 13px;
    color: #898989
  }
}
.tooltip {
  background-color: rgba(0, 0, 0, 0.6);
  color: #fff;
  padding: 5px;
  box-shadow: none;
  & div {
    font-size: 11px;
    line-height: 1;
  }
}
.asideActions {
  position: absolute;
  top: 100%;
  margin-top: 10px;
  left: -10px;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  gap: 5px;
  background-color: rgba(255, 255, 255, .8);
  padding: 10px;
  border-radius: 5px;
}
</style>
